import { useMemo } from 'react';
import { registerQuery, useQuery } from 'data-fetching-client';
import { createRpcClientV2 } from 'rpc-client-utils';
import http from 'hub-http/clients/apiClient';

import { resolveBreezeAgentNamesRpc } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsMetadataRpc';
import { captureException } from '../utils/sentryUtils';
import type { BreezeAgentRecord } from '../types/CrmSearchUser';

const chirpClient = createRpcClientV2({ hubHttpClient: http });

const BREEZE_AGENTS_FIELD = 'breezeAgents';

const EMPTY_AGENTS_RECORD: BreezeAgentRecord = {};

// Keep this in sync with ResolveBreezeAgentNamesRequestIF.MAX_AGENT_IDS. An
// approval template can contain more agents than an individual requester flow.
export const MAX_BREEZE_AGENT_IDS_PER_REQUEST = 50;

const normalizeAgentIds = (agentIds: readonly string[]): string[] =>
  [...new Set(agentIds)].sort();

const getAgentIdBatches = (agentIds: readonly string[]): string[][] => {
  const batches: string[][] = [];
  for (
    let startIndex = 0;
    startIndex < agentIds.length;
    startIndex += MAX_BREEZE_AGENT_IDS_PER_REQUEST
  ) {
    batches.push(
      agentIds.slice(startIndex, startIndex + MAX_BREEZE_AGENT_IDS_PER_REQUEST)
    );
  }
  return batches;
};

const FETCH_BREEZE_AGENTS = registerQuery<
  typeof BREEZE_AGENTS_FIELD,
  { agentIds: string[] },
  BreezeAgentRecord
>({
  fieldName: BREEZE_AGENTS_FIELD,
  args: ['agentIds'],
  fetcher: async ({ agentIds }: { agentIds: string[] }) => {
    const requestedAgentIds = new Set(agentIds);
    const responses = await Promise.all(
      getAgentIdBatches(agentIds).map(async (agentIdBatch) => {
        try {
          return await chirpClient.call(resolveBreezeAgentNamesRpc, {
            request: { agentIds: agentIdBatch },
          });
        } catch (err) {
          captureException(err as Error, {
            extra: {
              fingerprint: 'useBreezeAgents-resolveBreezeAgentNames',
              agentIds: agentIdBatch,
            },
          });
          return undefined;
        }
      })
    );

    const agentsRecord: BreezeAgentRecord = {};
    for (const response of responses) {
      for (const agent of response?.agents ?? []) {
        const displayName = agent.displayName?.trim();
        // The server echoes the requested ID. Still restrict the record to
        // this query's IDs so an unexpected response cannot affect another
        // approver's display name.
        if (requestedAgentIds.has(agent.agentId) && displayName) {
          agentsRecord[agent.agentId] = { displayName };
        }
      }
    }
    return agentsRecord;
  },
});

export interface UseBreezeAgents {
  agentsRecord: BreezeAgentRecord;
  loading: boolean;
}

export const useBreezeAgents = ({
  agentIds,
}: {
  agentIds: readonly string[];
}): UseBreezeAgents => {
  // The DFC query key must not depend on display order or duplicate actors.
  // This also means each unique agent is requested only once per view.
  const normalizedAgentIds = useMemo(
    () => normalizeAgentIds(agentIds),
    [agentIds]
  );
  const { data, loading } = useQuery(FETCH_BREEZE_AGENTS, {
    variables: { agentIds: normalizedAgentIds },
    skip: normalizedAgentIds.length === 0,
  });

  return {
    agentsRecord: data?.[BREEZE_AGENTS_FIELD] ?? EMPTY_AGENTS_RECORD,
    loading,
  };
};
